import { Box, SimpleGrid } from '@mantine/core';
import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { theme } from '@huspy/forge';
import { useForm, zodResolver } from '@mantine/form';
import useAddClient from '@modules/clients/entities/client/mutation/useAddClient';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { useTranslation } from 'react-i18next';
import { t as i18n } from 'i18next';
import { IS_SPAIN_ENV, slidoDeepLink } from '@modules/core/utils';
import {
  Text, Input, InputGroup, InputLabel, Button, InputDescription, PhoneNumberInput, Flex
} from '@huspy/briks-web';
import {
  Plus, User, Mail, Phone
} from '@huspy/briks-icons';
import briksTheme from '@shared/briks-theme';
import { ClientDetailsRoute } from '@modules/clients/presentation/routes';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { useDisclosure } from '@mantine/hooks';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import useGetClientByMobile from '@modules/clients/entities/client/query/useGetClientByMobile';
import useAssociateCompanyClient from '@modules/clients/entities/client/mutation/useAssociateCompanyClient';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { addClientSchema } from './validators';

const handleAddNewButtonClick = () => {
  trackAmplitudeEvent('Add New Client Clicked');
};

export type AddClientModalRef = { open(): void; close(): void } | undefined;

const invalidPhoneError = i18n('common.validation.invalidPhone');
const theSameClientFound = i18n('common.validation.theSameClientFound');

const ERROR_CODES = { theSameClientFound: 409 };

// eslint-disable-next-line react/display-name
const AddClientSlido = () => {
  const { t } = useTranslation();
  const { user } = usePersistUser();
  const { mutateAsync, isPending, error } = useAddClient(user?.brokerageId);
  const {
    fetch: fetchClientByMobile,
    data: duplicateClientData, isFetching: isFetchingClientByMobile,
  } = useGetClientByMobile(user?.brokerageId!);
  const [isDuplicateClient, setIsDuplicateClient] = useState(false);
  const { isFeatureActive } = useFeatureFlags();
  const isClientCompanyActive = isFeatureActive('clients.companyClients');
  const { mutateAsync: associateClient, isPending: isAssociatePending } = useAssociateCompanyClient(user?.brokerageId!);
  const navigate = useNavigate({ from: ClientDetailsRoute.fullPath });
  const { hash } = useLocation();
  const [isOpen, { open, close }] = useDisclosure(hash === slidoDeepLink.CLIENT_CREATION);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const form = useForm({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      mobile: {
        inputValue: '',
        phone: '',
        country: {},
      },
    },
    validateInputOnChange: true,
    validate: zodResolver(addClientSchema),
  });

  const handleFormResponse = useCallback(() => {
    if (error?.message?.includes('mobile')) {
      // eslint-disable-next-line sonarjs/no-duplicate-string
      form.setFieldError('mobile.inputValue', error?.message);
    }
    if (error?.message?.includes('email')) {
      form.setFieldError('email', error?.message);
    }

    if (IS_SPAIN_ENV && error?.statusCode === ERROR_CODES.theSameClientFound) {
      form.setFieldError('mobile', theSameClientFound);
      form.setFieldError('email', theSameClientFound);
    }

    if (error?.data?.validations) {
      error?.data?.validations.forEach((err: Record<string, unknown>) => {
        if (err.field === 'mobile') {
          // @ts-ignore
          form.setFieldError(`${err.field}.inputValue`, IS_SPAIN_ENV ? invalidPhoneError : err.errors.join('\n'));
        } else {
          // @ts-ignore
          form.setFieldError(err.field, err.errors.join('\n'));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    handleFormResponse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleFormSubmit = async () => {
    const { existsForBroker, existsInCompany } = await fetchClientByMobile(form.values.mobile.phone);

    if (isClientCompanyActive) {
      if (existsInCompany) {
        setIsDuplicateClient(true);
        form.reset();
        return;
      }
      if (existsForBroker) {
        form.setFieldError('mobile.inputValue', theSameClientFound);
        form.setFieldError('email', theSameClientFound);
        return;
      }
    }

    const res = await mutateAsync({
      ...form.values,
      mobile: form.values.mobile.phone,
    });
    form.reset();
    navigate({
      to: ClientDetailsRoute.to,
      params: { id: res.id },
    });
    closeButtonRef.current?.click();

    trackAmplitudeEvent('Client Added', { client_id: res.id });
  };

  const renderBody = () => {
    if (isDuplicateClient && isClientCompanyActive) {
      return (
        <BaseSlido
          opened={ isOpen }
          close={ () => {
            close();
            setIsDuplicateClient(false);
          } }
          title={ (
            <Flex p={ 4 } direction='column' gap={ 2 }>
              <Text size='2xl'>{t('clients.existingClient.title') }</Text>
            </Flex>
          ) }

        >
          <Flex p={ 4 } h='100%' gap='4' direction='column'>
            <Flex
              gap='4'
              align='start'
              bg='blue.50'
              p='2'
              borderRadius='2'
              direction='column'
            >
              <Flex direction='column' gap='2'>
                <Flex gap='2' align='center'>
                  <User width='20' height='20' />
                  <Text color='text-primary' size='lg'>
                    {duplicateClientData?.first_name }
                    {' '}
                    {duplicateClientData?.last_name }
                  </Text>
                </Flex>
                <Flex gap='2' align='center'>
                  <Mail width='20' height='20' />
                  <Text color='text-primary' size='lg'>
                    {duplicateClientData?.email }
                  </Text>
                </Flex>
                <Flex gap='2' align='center'>
                  <Phone width='20' height='20' />
                  <Text color='text-primary' size='lg'>
                    {duplicateClientData?.mobile }
                  </Text>
                </Flex>
              </Flex>
            </Flex>
            <Text color='text-primary' size='md'>
              {t('clients.existingClient.disclaimer') }
            </Text>
            <Flex gap='2'>
              <Button
                onClick={ async () => {
                  await associateClient(duplicateClientData?.id!);
                  close();
                  setIsDuplicateClient(false);
                  navigate({
                    to: ClientDetailsRoute.to,
                    params: { id: duplicateClientData?.id! },
                  });
                } }
                variant={ isAssociatePending ? 'secondary' : 'primary' }
                loading={ isAssociatePending }
                w='100%'
              >
                {t('clients.existingClient.confirmBtn') }
              </Button>
              <Button
                variant='secondary'
                onClick={ () => {
                  close();
                  setIsDuplicateClient(false);
                } }
                w='100%'
              >
                {t('clients.existingClient.declineBtn') }
              </Button>
            </Flex>
            <Flex
              bg='error.100'
              borderRadius='2'
            >
              <Text
                color='text-error-primary'
                p='2'
                size='md'
              >
                {t('clients.existingClient.error') }
                .
              </Text>
            </Flex>
          </Flex>
        </BaseSlido>
      );
    }
    return (
      <BaseSlido
        opened={ isOpen }
        close={ close }
        title={ (
          <Flex p={ 4 } direction='column' gap={ 2 }>
            <Text size='5xl'>{ t('clients.addClient.title') }</Text>
            <Text size='md' color='neutral.500'>{ t('clients.addClient.subtitle') }</Text>
          </Flex>
        ) }
        footer={ (
          <Flex justify='flex-end'>
            <Button
              type='submit'
              disabled={ isPending || isFetchingClientByMobile }
              loading={ isPending || isFetchingClientByMobile }
              variant={ isPending || isFetchingClientByMobile ? 'secondary' : 'primary' }
              onClick={ handleFormSubmit }
              data-test='add-client-btn'
            >
              { t('clients.addClient.ctaAdd') }
            </Button>
          </Flex>
        ) }
      >
        <Flex p={ 4 } h='100%'>
          <form>
            <Box>
              <SimpleGrid
                cols={ 2 }
                spacing={ theme.spacing.lg }
                verticalSpacing={ theme.spacing.xl }
              >
                <InputGroup isError={ !!form.getInputProps('first_name')?.error }>
                  <InputLabel>
                    {t('common.forms.firstName.label')}
                  </InputLabel>
                  <Input
                    name='first_name'
                    placeholder={ t('common.forms.firstName.placeholder') }
                    type='text'
                    disabled={ isPending }
                    { ...form.getInputProps('first_name') }
                    data-test='input-firstname'
                  />
                  <InputDescription>
                    {form.getInputProps('first_name')?.error && form.getInputProps('first_name').error}
                  </InputDescription>
                </InputGroup>
                <InputGroup isError={ !!form.getInputProps('last_name')?.error }>
                  <InputLabel>
                    { t('common.forms.lastName.label')}
                  </InputLabel>
                  <Input
                    name='last_name'
                    placeholder={ t('common.forms.lastName.placeholder') }
                    type='text'
                    disabled={ isPending }
                    { ...form.getInputProps('last_name') }
                    data-test='input-lastname'
                  />
                  <InputDescription>
                    {form.getInputProps('last_name')?.error && form.getInputProps('last_name').error}
                  </InputDescription>
                </InputGroup>
                <InputGroup isError={ !!form.getInputProps('email')?.error }>
                  <InputLabel>
                    {t('common.forms.email.label')}
                  </InputLabel>
                  <Input
                    name='email'
                    placeholder={ t('common.forms.email.placeholder') }
                    type='text'
                    disabled={ isPending }
                    { ...form.getInputProps('email') }
                    data-test='input-email'
                  />
                  <InputDescription>
                    {form.getInputProps('email')?.error && form.getInputProps('email').error}
                  </InputDescription>
                </InputGroup>
                {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
                <InputGroup isError={ !!form.getInputProps('mobile.inputValue')?.error } pos='relative'>
                  <InputLabel>
                    {t('common.forms.mobile.label')}
                  </InputLabel>
                  <PhoneNumberInput
                    name='mobile'
                    defaultCountry={ t('common.forms.mobile.defaultCountryPrefix') }
                    placeholder={ t('common.forms.mobile.placeholder') }
                    disabled={ isPending }
                    onChange={ (e) => {
                      form.getInputProps('mobile').onChange(e);
                      if (e.inputValue.length > 0) {
                        form.clearFieldError('mobile.inputValue');
                      }
                    } }
                    defaultValue={ form.getInputProps('mobile').value }
                    data-test='input-mobile'
                  />
                  <InputDescription>
                    {form.getInputProps('mobile.inputValue')?.error && form.getInputProps('mobile.inputValue').error}
                  </InputDescription>
                </InputGroup>
              </SimpleGrid>
            </Box>
          </form>
        </Flex>
      </BaseSlido>
    );
  };

  return (
    <>
      <Button
        onClick={ () => { handleAddNewButtonClick(); open(); } }
        data-test='add-new-btn'
      >
        <Plus width={ 18 } color={ briksTheme.colors['base.white'] } />
        { t('clients.addNew') }
      </Button>
      {renderBody()}
    </>
  );
};

export default AddClientSlido;
